import React, { useState } from "react";
import { Copy, Gift, MessageCircle, Share2, X } from "lucide-react";
import { GiftVoiceRecorder } from "./GiftVoice";
import { TogetherAsk } from "../together/TogetherAsk";

const SERIF = '"Cormorant Garamond", Georgia, serif';
const field = "w-full rounded-xl px-4 py-3 text-base outline-none";
const fieldStyle = { background: "rgba(241,236,221,0.05)", border: "1px solid rgba(215,187,122,0.25)", color: "#F1ECDD" };

// Gift card: personal note + phone share sheet (WhatsApp, Messages, email…), WhatsApp direct, or copy link.
export const GiftSheet = ({ title, url, journeyId, onClose }) => {
  const [ask, setAsk] = useState(!!journeyId);
  const [noteId, setNoteId] = useState(null);
  const [to, setTo] = useState("");
  const [from, setFrom] = useState("");
  const [note, setNote] = useState("I made this for you. Pop your headphones on tonight and let it play.");
  const [done, setDone] = useState("");
  const link = () => {
    const q = new URLSearchParams({ b: "soulumo" });
    if (to.trim()) q.set("for", to.trim());
    if (from.trim()) q.set("from", from.trim());
    if (note.trim()) q.set("m", note.trim());
    if (noteId) q.set("v", noteId);
    return `${url}?${q}`;
  };
  const message = () => `${to.trim() ? `${to.trim()}, ` : ""}${note.trim() || "I made this for you."}${noteId ? " (There's a voice message from me first.)" : ""} 🎁 "${title}"`;
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `A gift: ${title}`, text: message(), url: link() }); setDone("Sent"); } catch { /* closed */ }
    } else { window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${message()}\n${link()}`)}`, "_blank"); }
  };
  const whatsapp = () => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${message()}\n${link()}`)}`, "_blank");
  const copy = async () => { try { await navigator.clipboard.writeText(link()); setDone("Link copied"); } catch { setDone(link()); } };
  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center" style={{ background: "rgba(3,7,16,0.72)" }} onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="gift-sheet">
      <div className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 space-y-4 max-h-[92vh] overflow-y-auto" style={{ background: "#0a132a", border: "1px solid rgba(215,187,122,0.3)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[0.62rem] uppercase tracking-[0.3em]" style={{ color: "#D7BB7A" }}><Gift size={12} className="inline -mt-0.5 mr-1" /> Send to a friend</p>
            <h3 className="mt-1 text-2xl" style={{ fontFamily: SERIF, color: "#F1ECDD" }}>{title}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="gift-sheet-close" style={{ color: "rgba(241,236,221,0.6)" }}><X size={20} /></button>
        </div>
        {ask ? <TogetherAsk journeyId={journeyId} onJustGift={() => setAsk(false)} /> : <>
        <div className="grid grid-cols-2 gap-3">
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Their name" maxLength={40} className={field} style={fieldStyle} data-testid="gift-to" />
          <input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="From (you)" maxLength={40} className={field} style={fieldStyle} data-testid="gift-from" />
        </div>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={300} className={field} style={{ ...fieldStyle, fontFamily: SERIF }} data-testid="gift-note-input" />
        {journeyId && <GiftVoiceRecorder journeyId={journeyId} onNote={setNoteId} />}
        <button type="button" onClick={share} data-testid="gift-share" className="w-full rounded-full py-3.5 inline-flex items-center justify-center gap-2 text-sm tracking-[0.2em] uppercase" style={{ background: "#D7BB7A", color: "#0a132a" }}>
          <Share2 size={15} /> Send
        </button>
        <div className="grid grid-cols-2 gap-3">
          <button type="button" onClick={whatsapp} data-testid="gift-whatsapp" className="rounded-full py-3 inline-flex items-center justify-center gap-2 text-sm" style={{ border: "1px solid rgba(215,187,122,0.5)", color: "#D7BB7A" }}><MessageCircle size={15} /> WhatsApp</button>
          <button type="button" onClick={copy} data-testid="gift-copy" className="rounded-full py-3 inline-flex items-center justify-center gap-2 text-sm" style={{ border: "1px solid rgba(215,187,122,0.5)", color: "#D7BB7A" }}><Copy size={15} /> Copy link</button>
        </div>
        {done && <p className="text-center text-xs break-all" style={{ color: "rgba(241,236,221,0.6)" }} data-testid="gift-done">{done}</p>}
        <p className="text-center text-xs" style={{ color: "rgba(241,236,221,0.45)" }}>They can listen straight away, no account needed.</p>
        </>}
      </div>
    </div>
  );
};
